import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2 } from "lucide-react";

const EMPTY = { key:"", icon:"Code2", description:"", hero_description:"", core_skills:[], featured_image:"", display_order:100, active:true };

export default function AdminTechnologies() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const load = React.useCallback(()=>api.get("/admin/technologies").then(r=>setRows(r.data)),[]);
  useEffect(()=>{ load(); },[load]);
  const startNew = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const startEdit = (t) => { setEditing(t); setF({...t, core_skills: t.core_skills || []}); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...f, core_skills: Array.isArray(f.core_skills) ? f.core_skills : String(f.core_skills||"").split(",").map(s=>s.trim()).filter(Boolean) };
      if (editing) await api.put(`/admin/technologies/${editing.id}`, payload);
      else await api.post("/admin/technologies", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this technology?")) return;
    try { await api.delete(`/admin/technologies/${id}`); toast.success("Deleted"); load(); }
    catch(e){ toast.error("Failed"); }
  };
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">CMS</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Technologies</h1>
          <p className="text-slate-400 mt-1">Manage the technologies shown on the public Careers homepage.</p>
        </div>
        <Button onClick={startNew} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="tech-create-btn"><Plus className="w-4 h-4 mr-2"/>New Technology</Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(t => (
          <Card key={t.id} className="bg-slate-900 border-slate-800 p-5" data-testid={`tech-row-${t.key}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-display text-lg font-semibold text-white">{t.key}</div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={()=>startEdit(t)} className="text-slate-300 hover:text-white hover:bg-slate-800"><Edit2 className="w-3.5 h-3.5"/></Button>
                <Button size="sm" variant="ghost" onClick={()=>remove(t.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-2 line-clamp-3">{t.description}</p>
            <div className="mt-3 flex flex-wrap gap-1">
              {(t.core_skills || []).slice(0,5).map(s => <Badge key={s} className="bg-slate-700 text-white border-none hover:bg-slate-700 text-[10px]">{s}</Badge>)}
              {(t.core_skills || []).length > 5 && <Badge className="bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-800 text-[10px]">+{t.core_skills.length - 5} more</Badge>}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>Order: {t.display_order}</span>
              <Badge className={t.active ? "bg-emerald-500/20 text-emerald-400 border-none hover:bg-emerald-500/20" : "bg-slate-700 text-white border-none hover:bg-slate-700"}>{t.active ? "Active" : "Inactive"}</Badge>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Create"} technology</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Label className="text-slate-300">Name / Key</Label>
                <Input value={f.key} onChange={e=>setF({...f, key:e.target.value})} placeholder="Python" className="bg-slate-800 border-slate-700 text-white" data-testid="tech-form-key"/>
              </div>
              <div><Label className="text-slate-300">Display order</Label>
                <Input type="number" value={f.display_order} onChange={e=>setF({...f, display_order:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/>
              </div>
            </div>
            <div><Label className="text-slate-300">Short description</Label>
              <Textarea rows={2} value={f.description} onChange={e=>setF({...f, description:e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="tech-form-desc"/>
            </div>
            <div><Label className="text-slate-300">Hero description (optional)</Label>
              <Textarea rows={2} value={f.hero_description || ""} onChange={e=>setF({...f, hero_description:e.target.value})} className="bg-slate-800 border-slate-700 text-white"/>
            </div>
            <div><Label className="text-slate-300">Core skills (comma separated, 8-10 recommended)</Label>
              <Input value={Array.isArray(f.core_skills) ? f.core_skills.join(", ") : f.core_skills}
                onChange={e=>setF({...f, core_skills:e.target.value})}
                placeholder="Python, FastAPI, SQL, Docker..."
                className="bg-slate-800 border-slate-700 text-white" data-testid="tech-form-skills"/>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Icon (Lucide name)</Label>
                <Input value={f.icon || ""} onChange={e=>setF({...f, icon:e.target.value})} placeholder="Cpu" className="bg-slate-800 border-slate-700 text-white"/>
              </div>
              <div><Label className="text-slate-300">Featured image URL</Label>
                <Input value={f.featured_image || ""} onChange={e=>setF({...f, featured_image:e.target.value})} className="bg-slate-800 border-slate-700 text-white"/>
              </div>
            </div>
            <label className="flex items-center gap-2 pt-2 text-sm"><Switch checked={f.active} onCheckedChange={v=>setF({...f, active:v})}/>Active on homepage</label>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="tech-form-save">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
